import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Play, ChevronRight, ArrowRight, Bot, KeyRound, Sparkles, Globe, Shuffle, LayoutGrid, X as CloseIcon } from 'lucide-react';

import { TopBar, Btn, Avatar, IconButton, Page, WordRushLockup, PublicGames, useToast } from '../components/wr';
import { useGame, useWebSocket } from '../context/GameContext';
import { useAuth } from '../context/AuthContext';
import { useSurface } from '../hooks/useSurface';
import { useLiveQuery } from '../hooks/useLiveQuery';
import { websocketService } from '../services/websocketService';
import { buildGameTitle, buildGameSubtitle } from '../util/gameTitle';
import { todayLetter } from '../services/dailyChallengeService';
import { START_TABLE_INTENT } from './LandingScreen';
import { memberList, myTablesQuery, mapTables } from '../services/tablesService';
import { myGamesQuery, mapUserGames } from '../services/userHistoryService';
import {
  subscribePublicRooms,
  sortPublicRooms,
  resolveQuickMatch,
  type PublicRoom,
  type QuickMatchIntent,
} from '../services/publicRoomsService';

/** One row in "Jump back in" — a live game, an async turn, or an invite. */
interface JumpRow {
  key: string;
  kind: 'game' | 'invite';
  title: string;
  subtitle: string;
  badge?: { label: string; color: string };
  avatar?: string;
  route: string;
  onDismiss?: () => void;
}

interface HomeScreenProps {
  currentPlayer?: { id: string; name: string; avatar: string };
}

const HINT_KEY = 'wr.home.hintDismissed';

/**
 * Home.
 *
 * Rebuilt from eleven stacked sections down to six things:
 *   TopBar · hero Play card · three chips · Jump back in · Public games ·
 *   Your tables (or Recent games).
 *
 * Two structural bugs are fixed here, not papered over:
 *
 * 1. THE FIRST-RUN TRAP. This screen used to mount FirstRunDemoModal
 *    (z-250) and FirstTimeTutorial (z-240) on top of each other for every
 *    new account. The tutorial dialog intercepted every pointer event on
 *    the page, so a brand-new user's first interaction with WordRush was a
 *    modal they could not escape. Both components are DELETED. The game
 *    teaches itself in the loop; the rules live behind an opt-in link.
 *    **No overlay may ever appear before the user's first game.**
 *
 * 2. THE CTA OVERLAP. `.screen-pad` is `overflow-y: auto`, and the inner
 *    motion.div was ALSO `overflow-y: auto` — two nested scrollports. The
 *    docked `position: sticky` CTA therefore anchored to the wrong one and
 *    rendered on top of the Recent Games card. There is now exactly ONE
 *    scroll container (`.screen-pad`) and no docked CTA at all: the hero
 *    card *is* the call to action.
 */
export default function HomeScreen(_props: HomeScreenProps) {
  const navigate = useNavigate();
  const { state } = useGame();
  const { createRoom } = useWebSocket();
  const { currentUser, userProfile } = useAuth();
  /**
   * The ONE surface branch on this screen, and it decides one thing: whether
   * there is an aside column. Everything below reads `desktop` to route a
   * block into `main` or `aside` — it never renders a *different* block.
   * Same components, same data, two arrangements.
   *
   * The Daily graduates from a chip to an aside card here, because on a
   * laptop at 9am the Daily is the single most likely intent — while on a
   * phone, "play with the people in this room" still is.
   */
  const desktop = useSurface() === 'desktop';

  const toast = useToast();
  const [showJoin, setShowJoin] = useState(false);
  const [joinCode, setJoinCode] = useState('');
  const [creating, setCreating] = useState(false);
  const [activeGames, setActiveGames] = useState<any[]>([]);
  const [incomingInvites, setIncomingInvites] = useState<any[]>([]);
  const [publicRooms, setPublicRooms] = useState<PublicRoom[]>([]);
  const [matching, setMatching] = useState(false);
  const [hintDismissed, setHintDismissed] = useState<boolean>(() => {
    try { return localStorage.getItem(HINT_KEY) === '1'; } catch { return true; }
  });

  const player = state.currentPlayer;
  const isGuest = !player || (currentUser as any)?.isAnonymous;

  // Where new rooms go. Sourced from the user's own Settings → Privacy
  // preference rather than a control on this screen — Home shouldn't ask
  // you to configure anything before you can play.
  const visibility = userProfile?.privacy?.defaultGameVisibility || 'private';

  /**
   * PUBLIC GAMES — the live list of rooms you can actually walk into.
   *
   * Seeded from `GET /api/rooms/public` so it is populated on the first frame,
   * then kept live by `public_rooms_updated`. Both already existed on the
   * server; nothing in the app had ever listened.
   *
   * The seed matters for one specific reason: an empty list has to MEAN "there
   * are no games right now". It cannot also mean "we haven't heard back yet",
   * or the empty state is a lie half the time it renders.
   */
  useEffect(() => subscribePublicRooms(rooms => setPublicRooms(sortPublicRooms(rooms))), []);

  /**
   * QUICK MATCH — three outcomes, and the UI owes the truth on all three.
   *
   * `quickMatchEmpty` is the interesting one: the server does NOT create a room
   * for you, it tells you there was nobody to seat you with. So we make one, and
   * we say that's what happened. Dressing an empty pool up as a successful match
   * would be the exact fabrication this product refuses.
   */
  useEffect(() => {
    const svc: any = websocketService;

    // What to DO is decided by resolveQuickMatch (pure, tested). This only
    // carries it out.
    const apply = async (intent: QuickMatchIntent) => {
      switch (intent.action) {
        case 'join':
          setMatching(false);
          navigate(`/join/${intent.roomCode}`);
          return;

        case 'open-public':
          toast.info(intent.notice);
          try {
            // Public and quick-matchable, so the next person to hit Quick Match
            // lands HERE. This is the only way an empty pool becomes a full one.
            await createRoom({ isPrivate: false, isQuickMatch: true });
          } catch (e) {
            console.error('Quick match room creation failed', e);
            toast.error('Could not start a game. Try again?');
          } finally {
            setMatching(false);
          }
          return;

        case 'explain':
          setMatching(false);
          toast.error(intent.notice);
          return;
      }
    };

    const onFound = (d: any) => void apply(resolveQuickMatch({ kind: 'found', roomCode: d?.roomCode }));
    const onEmpty = (d: any) => void apply(resolveQuickMatch({ kind: 'empty', message: d?.message }));
    const onBlocked = (d: any) => void apply(resolveQuickMatch({ kind: 'blocked', message: d?.message }));

    svc.on('quickMatchFound', onFound);
    svc.on('quickMatchEmpty', onEmpty);
    svc.on('quickMatchBlocked', onBlocked);
    return () => {
      svc.off('quickMatchFound', onFound);
      svc.off('quickMatchEmpty', onEmpty);
      svc.off('quickMatchBlocked', onBlocked);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  // Active (in-progress) games for this user — refresh-recovery. Re-pulls
  // when the socket signals a connection or a room-state change.
  useEffect(() => {
    let cancelled = false;
    const refresh = async () => {
      const { fetchMyActiveGames } = await import('../services/activeGamesService');
      const games = await fetchMyActiveGames();
      if (!cancelled) setActiveGames(games);
    };
    refresh();
    // Event-driven, not polled. The public-rooms browser used to live on
    // this screen behind a 60s poll and the friends accordion behind a 30s
    // presence poll; both are gone with the sections they fed.
    const svc: any = websocketService;
    const onPlayersUpdated = () => refresh();
    svc.on('players_updated', onPlayersUpdated);
    svc.on('connect', refresh);
    /**
     * AND AGAIN ONCE THE SERVER KNOWS WHO WE ARE.
     *
     * "My active games" is a question that cannot be answered before the socket
     * has an identity — and `connect` fires BEFORE `authenticate` completes. So on
     * a cold page load this asked the server for the current user's games while
     * the server still had no current user, got an honest empty list, and never
     * asked again.
     *
     * A live game hid the bug: you arrive at Home from inside the app, socket long
     * since authenticated, and the mount-time fetch works. Mom arrives on a COLD
     * LOAD, hours later, and this list is the thing that is supposed to tell her it
     * is her turn — the one surface async cannot do without. It was empty every
     * time, for the one person who needed it.
     */
    svc.on('authenticated', refresh);
    return () => {
      cancelled = true;
      svc.off('players_updated', onPlayersUpdated);
      svc.off('connect', refresh);
      svc.off('authenticated', refresh);
    };
  }, []);

  // Pending game invites — live subscription.
  useEffect(() => {
    if (!currentUser?.uid) {
      setIncomingInvites([]);
      return;
    }
    let unsub: any;
    import('../services/gameInvitesService').then(m => {
      unsub = m.subscribeIncomingInvites(currentUser.uid, rows => setIncomingInvites(rows));
    }).catch(() => {});
    return () => { try { unsub?.(); } catch {} };
  }, [currentUser?.uid]);

  /**
   * YOUR TABLES — and the public-games feed this replaced.
   *
   * Home used to end on `/games/public`: other people's finished games, on a
   * family's home screen. That was noise AND a UGC surface — stranger names and
   * stranger answers, one tap from every kid in the house. It is gone, and the
   * Tables row takes its slot. Home stays at five sections; it does not grow.
   *
   * The two are mutually exclusive by design. A user with tables sees their
   * tables (each table's album supersedes a generic feed the moment it exists);
   * a user with none sees THEIR OWN recent games, which is their only proof of
   * life. `/history` keeps everything either way.
   *
   * BOTH ARE LIVE. This used to fetch once per sign-in, which meant the single
   * most important moment in the Tables feature — *somebody just added you to a
   * table* — was invisible until you happened to reload the page. Now the row
   * appears under you. The recent-games list is live for the same reason: an
   * async game you are in can end while you are sitting on Home.
   */
  const liveTables = useLiveQuery(myTablesQuery(currentUser?.uid || ''), mapTables);
  const tables = liveTables.data;

  const liveMyGames = useLiveQuery(
    // Only the fallback needs this — a user with tables never renders it, so we
    // hold no listener for it. (`null` query = no listener at all.)
    !liveTables.loading && tables.length === 0 && currentUser?.uid
      ? myGamesQuery(currentUser.uid, 8)
      : null,
    mapUserGames(currentUser?.uid || ''),
  );
  const myGames = liveMyGames.data;

  /**
   * "Jump back in" — live games, your-turn async games, and invites were
   * three separate sections competing for the same job ("get me back into
   * something"). They're one list now, capped at 3, and the whole block
   * disappears when it's empty instead of leaving a labelled void.
   */
  const jumpRows: JumpRow[] = useMemo(() => {
    const live: JumpRow[] = activeGames
      .filter((g: any) => g.status !== 'completed')
      .map((g: any) => ({
        key: `g-${g.roomCode}`,
        kind: 'game' as const,
        title: buildGameTitle({
          players: g.players || [],
          perspectiveUid: player?.id,
          isSolo: g.isSinglePlayer,
        }),
        subtitle: buildGameSubtitle({
          rounds: g.totalRounds || 0,
          mode: g.isSinglePlayer ? 'solo' : 'multiplayer',
          isAsync: g.isAsync,
          status: g.status,
          currentRound: g.currentRound,
          totalRounds: g.totalRounds,
          playersCount: (g.players || []).length,
        }),
        badge:
          g.yourTurn ? { label: 'Your turn', color: 'var(--accent)' } :
          g.status === 'playing' ? { label: `Round ${g.currentRound}/${g.totalRounds}`, color: 'var(--green)' } :
          { label: 'In lobby', color: 'var(--slate)' },
        route: g.status === 'lobby' || g.status === 'waiting' ? `/room/${g.roomCode}` : `/game/${g.roomCode}`,
      }));

    const invites: JumpRow[] = incomingInvites.map((inv: any) => ({
      key: `i-${inv.id}`,
      kind: 'invite' as const,
      title: `${inv.fromName} invited you`,
      subtitle: `${inv.mode === 'async' ? 'Turn-based' : 'Live'} · ${inv.roomCode}`,
      badge: { label: 'Invite', color: 'var(--accent)' },
      avatar: inv.fromAvatar,
      route: `/join/${inv.roomCode}`,
      onDismiss: () => {
        setIncomingInvites(prev => prev.filter((i: any) => i.id !== inv.id));
        import('../services/gameInvitesService')
          .then(m => m.setInviteStatus(inv.id, 'declined'))
          .catch(() => {});
      },
    }));

    // Your turn first — it's the only row that's actually blocking someone.
    return [...invites, ...live]
      .sort((a, b) => Number(b.badge?.label === 'Your turn') - Number(a.badge?.label === 'Your turn'))
      .slice(0, 3);
  }, [activeGames, incomingInvites, player?.id]);

  /**
   * The landing's "Start a table — free, no app" parks an intent in
   * sessionStorage and lets auth carry the visitor here (App routes an
   * authenticated user off `/`, which unmounts the landing mid-flow — so the
   * landing cannot own this). We pick the intent up on arrival, once the
   * player actually exists, and create the room from the path that already
   * works. Consume it first: a failed attempt must not retry forever.
   */
  useEffect(() => {
    if (!player?.id) return;
    if (sessionStorage.getItem(START_TABLE_INTENT) !== '1') return;
    sessionStorage.removeItem(START_TABLE_INTENT);
    void handlePlay();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [player?.id]);

  const handlePlay = async () => {
    if (creating) return;
    setCreating(true);
    try {
      // ONE room. The old six-card mode strip offered "1v1 with friend",
      // "Up to 8 players" and "Play with family" as separate choices — all
      // three called createRoom with identical arguments. Who shows up is
      // decided by who you send the link to, not by a card you tap here.
      //
      // AND STILL ONE TAP. Play does not stop to ask who may join: it makes the
      // room the user's own default says to make, and the lobby's visibility
      // card — the next thing they see — is where they change their mind. A
      // dialog in front of the primary CTA would cost every host a decision to
      // save a few of them a tap.
      //
      // A guest is always invite-only regardless of the stored default: hosting
      // strangers requires an account (the server refuses otherwise), and
      // bouncing someone off the Play button with a sign-in error is not an
      // answer. They get the room they can actually have.
      const code = (await createRoom({
        isPrivate: visibility === 'private' || isGuest,
      })) as unknown as string;
      if (code) {
        try { await navigator.clipboard.writeText(`${window.location.origin}/join/${code}`); } catch {}
      }
      // Navigation to the lobby is driven by state.roomCode in App.tsx.
    } catch (e) {
      console.error('Failed to create room', e);
      setCreating(false);
    }
  };

  /** Seat me in an open public game — or, if there are none, make one. The
      server decides which; we listen for all three answers above. */
  const handleQuickMatch = () => {
    if (matching || creating) return;
    if (isGuest) {
      navigate('/login');
      return;
    }
    setMatching(true);
    websocketService.quickMatch();
  };

  /**
   * The empty state's action, and the only honest one available.
   *
   * When nobody is playing, the fix for an empty room list is not to invent a
   * room — it is to open yours. The pool has to start somewhere, and this is
   * literally the mechanism by which it does.
   */
  const handleOpenToAnyone = async () => {
    if (creating) return;
    if (isGuest) {
      navigate('/login');
      return;
    }
    setCreating(true);
    try {
      await createRoom({ isPrivate: false, isQuickMatch: true });
    } catch (e) {
      console.error('Failed to open a public room', e);
      toast.error('Could not start a game. Try again?');
      setCreating(false);
    }
  };

  const handleJoin = () => {
    const code = joinCode.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
    if (!code) return;
    navigate(`/join/${code}`);
  };

  const dismissHint = () => {
    setHintDismissed(true);
    try { localStorage.setItem(HINT_KEY, '1'); } catch {}
  };

  /* ---- The blocks. Defined once, arranged twice. ------------------------
     Nothing below is a "desktop version" of anything — these are the exact
     same nodes the phone renders; `desktop` only decides which column they
     land in. */

  /* "Jump back in" — live games, your-turn async games, invites. On a phone
     it sits under the chips; on a laptop it moves to the aside, because the
     main column has room to stay two decisions deep. */
  const jumpBlock = jumpRows.length > 0 ? (
    <div className="wr-col gap-2">
      <div className="wr-row gap-2" style={{ paddingLeft: 4, alignItems: 'center' }}>
        <Play size={13} color="var(--accent)" />
        <span className="eyebrow accent">Jump back in</span>
      </div>
      <div className="wr-col gap-2">
        {jumpRows.map(row => (
          <div
            key={row.key}
            className="card"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: 12,
              border: '1.5px solid var(--accent)',
              background: 'var(--accent-soft)',
            }}
          >
            <button
              type="button"
              onClick={() => navigate(row.route)}
              className="wr-row gap-3"
              style={{
                flex: 1,
                minWidth: 0,
                alignItems: 'center',
                background: 'transparent',
                border: 'none',
                padding: 0,
                textAlign: 'left',
                cursor: 'pointer',
                color: 'inherit',
              }}
            >
              {row.avatar ? (
                <Avatar id={row.avatar} size={40} />
              ) : (
                <span
                  className="wr-center"
                  style={{ width: 40, height: 40, borderRadius: 12, background: 'var(--accent)', color: '#fff', flexShrink: 0 }}
                >
                  <Play size={18} strokeWidth={2.6} />
                </span>
              )}
              <span style={{ flex: 1, minWidth: 0 }}>
                <span className="wr-row gap-2" style={{ alignItems: 'center', flexWrap: 'wrap' }}>
                  <span
                    style={{
                      fontFamily: 'var(--display)',
                      fontWeight: 700,
                      fontSize: 15,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      maxWidth: 170,
                    }}
                  >
                    {row.title}
                  </span>
                  {row.badge && (
                    <span
                      style={{
                        fontSize: 13,
                        fontWeight: 700,
                        padding: '1px 8px',
                        borderRadius: 6,
                        background: row.badge.color,
                        color: '#fff',
                        letterSpacing: '0.01em',
                      }}
                    >
                      {row.badge.label}
                    </span>
                  )}
                </span>
                <span style={{ display: 'block', fontSize: 13, color: 'var(--ink-3)', marginTop: 2 }}>
                  {row.subtitle}
                </span>
              </span>
            </button>
            {row.onDismiss ? (
              <button
                type="button"
                onClick={row.onDismiss}
                aria-label="Decline invite"
                className="wr-center"
                style={{ width: 44, height: 44, background: 'transparent', border: 'none', color: 'var(--ink-3)', cursor: 'pointer', flexShrink: 0 }}
              >
                <CloseIcon size={16} />
              </button>
            ) : (
              <ChevronRight size={18} color="var(--accent)" style={{ flexShrink: 0 }} />
            )}
          </div>
        ))}
      </div>
    </div>
  ) : null;

  /* PUBLIC GAMES lives in components/wr — see PublicGames.tsx, where the
     empty-state honesty rule is written down and tested. Home only supplies the
     data and the verbs. */
  const publicBlock = (
    <PublicGames
      rooms={publicRooms}
      isGuest={isGuest}
      matching={matching}
      onJoin={code => navigate(`/join/${code}`)}
      onQuickMatch={handleQuickMatch}
      onOpenToAnyone={handleOpenToAnyone}
    />
  );

  /* The Daily. A chip on a phone, a card in the aside on a laptop. Same
     destination, same live letter — a different weight, because the intent
     that brings you here is different. */
  const dailyCard = (
    <button
      type="button"
      onClick={() => navigate('/daily')}
      className="card lg wr-col gap-3"
      style={{
        border: 'none',
        cursor: 'pointer',
        textAlign: 'left',
        alignItems: 'stretch',
        background: 'linear-gradient(155deg, var(--surface), var(--bg-2))',
      }}
    >
      <span className="wr-row gap-2" style={{ alignItems: 'center' }}>
        <Sparkles size={13} color="var(--accent)" />
        <span className="eyebrow accent">Today's Daily</span>
      </span>
      <span className="wr-row gap-3" style={{ alignItems: 'center' }}>
        <span
          className="wr-center"
          style={{
            width: 56,
            height: 56,
            borderRadius: 16,
            background: 'var(--hero-grad)',
            boxShadow: 'inset 0 0 0 1px var(--line)',
            fontFamily: 'var(--display)',
            fontWeight: 800,
            fontSize: 30,
            letterSpacing: '-0.04em',
            flexShrink: 0,
          }}
        >
          {todayLetter()}
        </span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: 'block', fontFamily: 'var(--display)', fontWeight: 700, fontSize: 17 }}>
            Letter {todayLetter()}
          </span>
          <span style={{ display: 'block', fontSize: 13, color: 'var(--ink-3)', marginTop: 2, lineHeight: 1.4 }}>
            Four words, no clock. Everyone gets the same letter.
          </span>
        </span>
        <ChevronRight size={18} color="var(--ink-3)" style={{ flexShrink: 0 }} />
      </span>
    </button>
  );

  return (
    <Page variant={desktop ? 'max' : 'content'}>
      {/* ONE scroll container (Page's `.screen-pad`). Do not add `overflow-y`
          to anything inside it — that's what broke the docked CTA's sticky
          positioning. */}
      <>
        <TopBar
          left={<WordRushLockup size={16} showByline={false} />}
          right={
            player ? (
              <button
                onClick={() => navigate('/profile')}
                aria-label="Your profile"
                className="wr-center"
                style={{
                  width: 44,
                  height: 44,
                  background: 'transparent',
                  border: 'none',
                  borderRadius: '50%',
                  cursor: 'pointer',
                }}
              >
                <Avatar id={player.avatar} size={32} />
              </button>
            ) : (
              <IconButton icon="LogIn" ariaLabel="Sign in" onClick={() => navigate('/login')} />
            )
          }
        />

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className={desktop ? 'wr-home' : 'wr-col gap-4'}
          style={{ padding: '8px 0' }}
        >
        <div className="home-main wr-col gap-4">
          {/* ---- 1. The hero. The only shadow-lg element on the screen, and
                  the only thing a first-time user has to understand. It is
                  the SAME component at 1280 as at 393 — just wider. Absolute
                  dominance of the main column is the point; a laptop doesn't
                  earn a second, competing primary action. ---- */}
          <button
            type="button"
            onClick={handlePlay}
            disabled={creating}
            aria-label="Start a game"
            style={{
              position: 'relative',
              textAlign: 'left',
              border: 'none',
              borderRadius: 'var(--r-lg)',
              padding: 24,
              cursor: creating ? 'progress' : 'pointer',
              background: 'linear-gradient(150deg, var(--accent-2), var(--accent))',
              color: '#fff',
              boxShadow: 'var(--shadow-glow)',
              overflow: 'hidden',
              width: '100%',
            }}
          >
            <span
              aria-hidden
              style={{
                position: 'absolute',
                inset: 0,
                background:
                  'radial-gradient(circle at 85% 15%, rgba(255,255,255,0.18), transparent 55%)',
                pointerEvents: 'none',
              }}
            />
            <span className="wr-row gap-4" style={{ position: 'relative', alignItems: 'center' }}>
              <span
                className="wr-center"
                style={{
                  width: 56,
                  height: 56,
                  borderRadius: 18,
                  background: 'rgba(255,255,255,0.18)',
                  flexShrink: 0,
                }}
              >
                <Play size={26} strokeWidth={2.6} fill="#fff" />
              </span>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span
                  style={{
                    display: 'block',
                    fontFamily: 'var(--display)',
                    fontWeight: 700,
                    fontSize: 28,
                    letterSpacing: '-0.03em',
                    lineHeight: 1.05,
                  }}
                >
                  {creating ? 'Creating room…' : 'Play'}
                </span>
                {/* One short line. The old subtitle ("Private 4-round game ·
                    90s rounds · share link copied for you") wrapped into
                    three lines of mush at 360px. */}
                <span
                  style={{
                    display: 'block',
                    fontSize: 15,
                    fontWeight: 500,
                    opacity: 0.92,
                    marginTop: 4,
                  }}
                >
                  {visibility === 'public' ? 'Anyone can join' : 'Invite family & friends'}
                </span>
              </span>
              <ArrowRight size={22} strokeWidth={2.6} style={{ opacity: 0.9, flexShrink: 0 }} />
            </span>
          </button>

          {/* ---- 2. Everything else you might do, as chips. This is the whole
                  replacement for the six-card mode scroller. On desktop the
                  Daily chip is GONE from this row — it has graduated to the
                  aside, and leaving a duplicate here would be two doors to
                  one room. ---- */}
          <div className="wr-row gap-2">
            {[
              { key: 'solo', label: 'Solo', icon: <Bot size={16} strokeWidth={2.4} />, go: () => navigate('/solo') },
              // The shelf. "Play" is still one tap and still plays Classic — a pack
              // is a door you choose to open, never a decision in front of the
              // primary CTA.
              { key: 'packs', label: 'Games', icon: <LayoutGrid size={16} strokeWidth={2.4} />, go: () => navigate('/packs') },
              { key: 'join', label: 'Join with code', icon: <KeyRound size={16} strokeWidth={2.4} />, go: () => setShowJoin(v => !v) },
              ...(desktop ? [] : [
                { key: 'daily', label: `Daily · ${todayLetter()}`, icon: <Sparkles size={16} strokeWidth={2.4} />, go: () => navigate('/daily') },
              ]),
            ].map(chip => (
              <button
                key={chip.key}
                type="button"
                onClick={chip.go}
                className="card wr-center gap-2"
                style={{
                  flex: 1,
                  minHeight: 44,
                  padding: '10px 8px',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: 13,
                  fontWeight: 600,
                  fontFamily: 'var(--ui)',
                  color: 'var(--ink-2)',
                  whiteSpace: 'nowrap',
                }}
              >
                {chip.icon}
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{chip.label}</span>
              </button>
            ))}
          </div>

          {/* Join-by-code opens INLINE (not as a modal) directly under the
              chip that summoned it. */}
          {showJoin && (
            <div className="card wr-col gap-3">
              <label className="eyebrow" htmlFor="wr-join-code">Room code</label>
              <input
                id="wr-join-code"
                className="field"
                placeholder="BLUEPEACH37"
                value={joinCode}
                onChange={e => setJoinCode(e.target.value.toUpperCase())}
                autoFocus
                style={{ textTransform: 'uppercase', fontFamily: 'var(--display)', fontWeight: 600 }}
                onKeyDown={e => { if (e.key === 'Enter') handleJoin(); }}
              />
              <div className="wr-row gap-2">
                <Btn kind="primary" full icon="ArrowRight" onClick={handleJoin}>Join</Btn>
                <Btn kind="ghost" onClick={() => { setShowJoin(false); setJoinCode(''); }}>Cancel</Btn>
              </div>
            </div>
          )}

          {/* ---- 3. Jump back in. Phone only — on a laptop this block lives
                  in the aside (see below). Rendered from the same `jumpBlock`
                  node either way; nothing is duplicated. ---- */}
          {!desktop && jumpBlock}

          {/* ---- 4. PUBLIC GAMES. Strangers, which the family product now
                  supports on purpose.

                  It sits here — above Your Tables — because it is an ACTION
                  ("get me into a game"), and the sections above it are actions
                  too. Tables and Recent Games are REFERENCE: your people, your
                  history. The screen reads do-things-then-look-at-things.

                  It does NOT replace the Tables row below. The public-games feed
                  that used to occupy that slot was a feed of other people's
                  FINISHED games — noise. This is a list of rooms you can walk
                  into right now, which is a different object entirely, and both
                  belong. ---- */}
          {publicBlock}

          {/* ---- 5. YOUR TABLES. Avatar-stack chips, max four, most recently
                  played first — so "play with Mom" is Home → chip → Start. Two
                  taps, which is the whole promise of the primitive.

                  There is no "new table" button here or anywhere else — a table
                  is kept from the results of a night you played, never created
                  from a menu. ---- */}
          {tables.length > 0 && (
            <div className="wr-col gap-2 mt-2">
              <div className="wr-row" style={{ paddingLeft: 4, alignItems: 'center', justifyContent: 'space-between' }}>
                <span className="eyebrow">Your tables</span>
                {tables.length > 4 && (
                  <button
                    type="button"
                    onClick={() => navigate('/tables')}
                    style={{ fontSize: 13, minHeight: 44, color: 'var(--accent)', background: 'transparent', border: 'none', cursor: 'pointer', fontWeight: 600 }}
                  >
                    All →
                  </button>
                )}
              </div>
              <div className="wr-row gap-2" style={{ overflowX: 'auto', paddingBottom: 2 }}>
                {tables.slice(0, 4).map(t => {
                  const members = memberList(t);
                  return (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => navigate(`/tables/${t.id}`)}
                      className="card wr-col gap-2"
                      style={{
                        flex: '1 1 0',
                        minWidth: 104,
                        padding: 12,
                        border: 'none',
                        cursor: 'pointer',
                        alignItems: 'flex-start',
                        textAlign: 'left',
                      }}
                    >
                      <span className="wr-row" style={{ minHeight: 30 }}>
                        {members.slice(0, 3).map((m, i) => (
                          <span key={m.uid} style={{ marginLeft: i === 0 ? 0 : -9, display: 'inline-flex' }}>
                            <Avatar id={m.avatar} size={30} />
                          </span>
                        ))}
                        {members.length > 3 && (
                          <span
                            className="wr-center"
                            style={{
                              marginLeft: -9,
                              width: 30,
                              height: 30,
                              borderRadius: 10,
                              background: 'var(--surface-2)',
                              color: 'var(--ink-3)',
                              fontSize: 11,
                              fontWeight: 700,
                            }}
                          >
                            +{members.length - 3}
                          </span>
                        )}
                      </span>
                      <span
                        style={{
                          display: 'block',
                          width: '100%',
                          fontFamily: 'var(--display)',
                          fontWeight: 700,
                          fontSize: 14,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {t.name}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Table-less users see THEIR OWN recent games — not strangers'. It's
              their only proof of life, and it disappears the moment a table
              exists, because the table's album supersedes it. */}
          {tables.length === 0 && myGames.length > 0 && (
            <div className="wr-col gap-2 mt-2">
              <div className="wr-row" style={{ paddingLeft: 4, alignItems: 'center', justifyContent: 'space-between' }}>
                <span className="eyebrow">Recent games</span>
                <button
                  type="button"
                  onClick={() => navigate('/history')}
                  style={{ fontSize: 13, minHeight: 44, color: 'var(--accent)', background: 'transparent', border: 'none', cursor: 'pointer', fontWeight: 600 }}
                >
                  View all →
                </button>
              </div>
              <div className="wr-col gap-1">
                {myGames.slice(0, 3).map(g => {
                  const title = buildGameTitle({
                    players: [
                      ...(player ? [{ id: player.id, name: player.name, isBot: false }] : []),
                      ...g.opponents.map(o => ({ id: o.id, name: o.name, isBot: o.isBot })),
                    ],
                    perspectiveUid: player?.id,
                    isSolo: g.mode === 'solo',
                  });
                  const subtitle = buildGameSubtitle({
                    rounds: g.rounds,
                    mode: g.mode,
                    playersCount: g.opponents.filter(o => !o.isBot).length + 1,
                  });
                  const face = g.opponents[0];
                  return (
                    <button
                      key={g.gameId}
                      onClick={() => navigate(`/games/${g.gameId}`)}
                      className="card"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        padding: 10,
                        width: '100%',
                        border: 'none',
                        textAlign: 'left',
                        cursor: 'pointer',
                      }}
                    >
                      {face ? (
                        <Avatar id={face.avatar} size={32} />
                      ) : (
                        <div style={{ width: 32, height: 32, borderRadius: 10, background: 'var(--surface-2)' }} />
                      )}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontFamily: 'var(--display)', fontWeight: 700, fontSize: 14, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {title} · <span style={{ color: 'var(--accent)', fontWeight: 800 }}>{g.yourScore}</span>
                        </div>
                        <div style={{ fontSize: 13, color: 'var(--ink-3)', marginTop: 1 }}>{subtitle}</div>
                      </div>
                      <ChevronRight size={16} color="var(--ink-3)" style={{ flexShrink: 0 }} />
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* ---- 6. The only thing resembling onboarding: one dismissible
                  INLINE line. Not a modal, not an overlay, doesn't block a
                  single pixel, and it's gone forever once tapped away. ---- */}
          {!hintDismissed && (
            <div
              className="wr-row gap-2"
              style={{
                alignItems: 'center',
                padding: '10px 12px',
                borderRadius: 'var(--r-sm)',
                background: 'var(--bg-2)',
                marginTop: 4,
              }}
            >
              <span style={{ flex: 1, fontSize: 13, color: 'var(--ink-3)', lineHeight: 1.35 }}>
                Name · Place · Animal · Thing — one letter, one minute.{' '}
                <button
                  type="button"
                  onClick={() => navigate('/how-it-works')}
                  style={{ background: 'transparent', border: 'none', padding: 0, color: 'var(--accent)', fontWeight: 600, fontSize: 13, cursor: 'pointer', fontFamily: 'var(--ui)' }}
                >
                  How it works
                </button>
              </span>
              <button
                type="button"
                onClick={dismissHint}
                aria-label="Dismiss"
                className="wr-center"
                style={{ width: 44, height: 44, margin: '-10px -8px -10px 0', background: 'transparent', border: 'none', color: 'var(--ink-3)', cursor: 'pointer', flexShrink: 0 }}
              >
                <CloseIcon size={14} />
              </button>
            </div>
          )}

          {/* Guests: a single text link, not a card-sized banner. */}
          {isGuest && (
            <button
              type="button"
              onClick={() => navigate('/login')}
              className="wr-row wr-center gap-2"
              style={{
                minHeight: 44,
                fontSize: 13,
                color: 'var(--ink-3)',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                fontFamily: 'var(--ui)',
              }}
            >
              <span>Sign in to keep your games, stats and friends</span>
              <ChevronRight size={14} />
            </button>
          )}
        </div>

        {/* ---- The aside. Desktop only, and it exists for exactly one
                reason: on a laptop the Daily is the most likely intent, and
                "get me back into the thing I was already doing" is the
                second. Neither of them earns a place ahead of Play — so they
                sit beside it, not above it. Nothing else moves in here.
                No chat rail, no stats panel.

                Public games stays in the MAIN column on both surfaces. It is a
                primary action — a room you can walk into right now — and the
                aside is for the two things that aren't. ---- */}
        {desktop && (
          <div className="home-aside">
            {dailyCard}
            {jumpBlock}
          </div>
        )}
        </motion.div>
      </>

      {/* No modals. No overlays. Nothing between a new player and Play. */}
    </Page>
  );
}
